import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_UPLOAD_SIZE_BYTES } from '~constants/common';

import {
  AcoDetectResponse,
  AcoResolveRequest,
  useAcoDetectMutation,
  useAcoResolveMutation,
  useAcoUploadMutation,
} from '~support-at-home/actions/acoBudgets';

export type WizardState = 'upload' | 'reviewing' | 'resolving' | 'complete';

export interface UploadMultipleRef {
  isComplete: boolean;
  hasUnresolvedDuplicates: boolean;
}

export interface BudgetError {
  id: number;
  line: number;
  reason: string;
}

interface UseAcoBudgetUploadOptions {
  onChange?: (field: string, value: UploadMultipleRef) => void;
}

export const useAcoBudgetUpload = ({ onChange }: UseAcoBudgetUploadOptions) => {
  // Standard upload (backward-compatible path when no duplicates)
  const [acoUpload, uploadResult] = useAcoUploadMutation();
  const { data: uploadData, isError: isUploadError, error: uploadError, isSuccess: isUploadSuccess } = uploadResult;

  // Detect + resolve mutations for duplicate wizard
  const [acoDetect, detectResult] = useAcoDetectMutation();
  const { data: detectData, isError: isDetectError, error: detectError, isSuccess: isDetectSuccess } = detectResult;

  const [acoResolve, resolveResult] = useAcoResolveMutation();
  const {
    isError: isResolveError,
    error: resolveError,
    isSuccess: isResolveSuccess,
    isLoading: isResolving,
  } = resolveResult;

  // State
  const [wizardState, setWizardState] = useState<WizardState>('upload');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<BudgetError[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [isErrorsOpen, setIsErrorsOpen] = useState(false);
  const [loserSelections, setLoserSelections] = useState<Record<number, Set<string>>>({});

  const hasTriggeredUpload = useRef(false);

  // Notify parent (e.g. WizardForm) of completion state
  const notifyParent = useCallback(
    (isComplete: boolean, hasUnresolvedDuplicates = false) => {
      onChange?.('aco_budgets_upload', { isComplete, hasUnresolvedDuplicates });
    },
    [onChange],
  );

  // Upload file for duplicate detection
  useEffect(() => {
    if (!file || hasTriggeredUpload.current) return;

    setUploadProgress(0);
    setApiError(null);
    setValidationErrors([]);
    hasTriggeredUpload.current = true;

    const formData = new FormData();
    formData.append('file', file);

    acoDetect({ formData, onProgress: setUploadProgress });
  }, [file, acoDetect]);

  // Handle detect success -- route to review or direct upload
  useEffect(() => {
    if (isDetectSuccess && detectData) {
      if (detectData.duplicates.length > 0) {
        // Initialize loser selections -- all existing records pre-ticked
        const initialSelections: Record<number, Set<string>> = {};
        detectData.duplicates.forEach((group, idx) => {
          initialSelections[idx] = new Set(group.existing.map((ex) => ex.id));
        });
        setLoserSelections(initialSelections);
        setWizardState('reviewing');
        notifyParent(false, true);
      } else {
        // No duplicates — fire standard upload with the same file. This re-uploads the CSV
        // (once for /detect, once for /aco-upload), but the file is already in browser memory
        // and typically <10 MB. Avoiding the double upload would require coupling detect and
        // import on the API side, which was intentionally kept separate.
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          acoUpload({ formData, onProgress: setUploadProgress });
        }
      }
    }
  }, [isDetectSuccess, detectData, file, acoUpload, notifyParent]);

  // Handle detect errors
  useEffect(() => {
    if (isDetectError && detectError) {
      const errorData = detectError as any;
      const message = errorData?.data?.message || errorData?.message || 'An unexpected error occurred';
      setApiError(message);

      const errors = errorData?.data?.errors || [];
      const errorsWithIds = errors.map((err: BudgetError, idx: number) => ({ ...err, id: idx + 1 }));
      setValidationErrors(errorsWithIds);

      setUploadProgress(0);
      setFile(null);
      hasTriggeredUpload.current = false;
    }
  }, [isDetectError, detectError]);

  // Handle standard upload success (no-duplicates path)
  useEffect(() => {
    if (isUploadSuccess && uploadData) {
      setWizardState('complete');
      notifyParent(true);
    }
  }, [isUploadSuccess, uploadData, notifyParent]);

  // Handle standard upload errors
  useEffect(() => {
    if (isUploadError && uploadError) {
      const errorData = uploadError as any;
      const message = errorData?.data?.message || errorData?.message || 'An unexpected error occurred';
      setApiError(message);

      const errors = errorData?.data?.errors || [];
      const errorsWithIds = errors.map((err: BudgetError, idx: number) => ({ ...err, id: idx + 1 }));
      setValidationErrors(errorsWithIds);

      setUploadProgress(0);
      setFile(null);
      hasTriggeredUpload.current = false;
    }
  }, [isUploadError, uploadError]);

  // Handle resolve success
  useEffect(() => {
    if (isResolveSuccess) {
      setWizardState('complete');
      notifyParent(true);
    }
  }, [isResolveSuccess, notifyParent]);

  // Handle resolve errors
  useEffect(() => {
    if (isResolveError && resolveError) {
      const errorData = resolveError as any;
      const message = errorData?.data?.message || errorData?.message || 'An error occurred during merge resolution';
      setApiError(message);
      setWizardState('reviewing');
    }
  }, [isResolveError, resolveError]);

  // Derived state
  const isUploading = !!file && uploadProgress < 100 && !detectData && !uploadData && !apiError;
  const isProcessing = uploadProgress === 100 && !detectData && !uploadData && !apiError && wizardState === 'upload';
  const isComplete = wizardState === 'complete';
  const showUpload = wizardState === 'upload' && !isUploading && !isProcessing;

  // Handlers
  const handleFileChange = useCallback(
    (chosen: File) => {
      if (chosen.size > MAX_UPLOAD_SIZE_BYTES) {
        setApiError('Selected file exceeds the 10 MB limit. Please choose a smaller file.');
        setFile(null);
        hasTriggeredUpload.current = false;
        return;
      }

      setApiError(null);
      setFile(chosen);
    },
    [],
  );

  const handleUploadAnother = useCallback(() => {
    setFile(null);
    setUploadProgress(0);
    setApiError(null);
    setValidationErrors([]);
    setIsErrorsOpen(false);
    setWizardState('upload');
    setLoserSelections({});
    uploadResult.reset();
    detectResult.reset();
    resolveResult.reset();
    hasTriggeredUpload.current = false;
    notifyParent(false);
  }, [uploadResult, detectResult, resolveResult, notifyParent]);

  const handleLoserToggle = useCallback((groupIndex: number, loserId: string) => {
    setLoserSelections((prev) => {
      const updated = { ...prev };
      const current = new Set(updated[groupIndex]);
      if (current.has(loserId)) {
        current.delete(loserId);
      } else {
        current.add(loserId);
      }
      updated[groupIndex] = current;
      return updated;
    });
  }, []);

  const handleConfirmMerge = useCallback(() => {
    if (!detectData) return;

    setApiError(null);
    setWizardState('resolving');

    const request: AcoResolveRequest = {
      merges: detectData.duplicates.map((group, idx) => ({
        incoming_row: group.incoming,
        loser_ids: Array.from(loserSelections[idx] ?? []),
      })),
      clean_rows: detectData.clean_rows,
    };

    acoResolve(request);
  }, [detectData, loserSelections, acoResolve]);

  const getTotalLoserCount = useCallback(
    () => Object.values(loserSelections).reduce((sum, set) => sum + set.size, 0),
    [loserSelections],
  );

  return {
    // State
    wizardState,
    apiError,
    validationErrors,
    isErrorsOpen,
    setIsErrorsOpen,
    loserSelections,
    uploadProgress,
    file,
    // Mutation data
    detectData: detectData as AcoDetectResponse | undefined,
    // Derived state
    isUploading,
    isProcessing,
    isComplete,
    showUpload,
    isResolving,
    // Handlers
    handleFileChange,
    handleUploadAnother,
    handleLoserToggle,
    handleConfirmMerge,
    // Helpers
    getTotalLoserCount,
  };
};
